<template>
  <div style="background-color: #f1f4f7; min-height: 100vh">
    <breadcrumb :items="breadcrumbs"></breadcrumb>

    <v-container fluid class="px-6 py-4">
      <!-- Header Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center">
            <h2 class="text-h5 font-weight-bold mb-0" style="color: #212121">
              {{
                (surveyData && surveyData.goal && surveyData.goal.title) ||
                $t("esgPlatform.assessmentFarmer.responses")
              }}
            </h2>
            <v-btn
              color="#00BD73"
              dark
              class="text-caption font-weight-bold px-6"
              @click="goBack"
              elevation="0"
            >
              {{ $t("back") || "BACK" }}
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <div v-if="loading" class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="#00BD73"
          size="48"
        ></v-progress-circular>
      </div>
      <div
        v-else-if="
          !surveyData ||
          !normalizedResponses.length
        "
        class="text-center py-12"
      >
        <v-alert type="info" text border="left">
          {{ $t("esgPlatform.assessmentFarmer.noResponsesFound") }}
        </v-alert>
      </div>

      <div v-else style="background-color: #ffffff" class="px-5 py5">
        <!-- Title and Response Score -->
        <v-row class="mb-6">
          <v-col cols="12">
            <div class="d-flex justify-space-between align-start">
              <div>
                <ul
                  style="
                    list-style-type: none;
                    padding-left: 0;
                    margin-bottom: 0;
                  "
                >
                  <li class="mb-2 d-flex align-start">
                    <span
                      class="font-weight-bold text-body-1"
                      style="min-width: 130px; color: #212121"
                      >{{ $t("esgPlatform.goal") }}:</span
                    >
                    <span class="text-body-1" style="color: #616161">{{
                      surveyData.goal.title
                    }}</span>
                  </li>
                  <li class="mb-2 d-flex align-start">
                    <span
                      class="font-weight-bold text-body-1"
                      style="min-width: 130px; color: #212121"
                      >{{$t("esgPlatform.company")}}:</span
                    >
                    <span class="text-body-1" style="color: #616161">{{
                      getCompanyName
                    }}</span>
                  </li>
                  <li class="mb-2 d-flex align-start">
                    <span
                      class="font-weight-bold text-body-1"
                      style="min-width: 130px; color: #212121"
                      >{{$t("esgPlatform.submittedBy")}}:</span
                    >
                    <span class="text-body-1" style="color: #616161">{{
                      getSubmittedBy
                    }}</span>
                  </li>
                  <li class="d-flex align-start">
                    <span
                      class="font-weight-bold text-body-1"
                      style="min-width: 130px; color: #212121"
                      >{{$t("esgPlatform.dateSubmitted")}}:</span
                    >
                    <span class="text-body-1" style="color: #616161">{{
                      getLatestSubmittedDate
                    }}</span>
                  </li>
                </ul>
              </div>
              <div class="text-right d-flex flex-column align-end">
                <div
                  class="font-weight-bold mb-1"
                  style="font-size: 16px; letter-spacing: 0.5px; color: #212121"
                >
                  {{ $t("esgPlatform.assessmentFarmer.responses").toUpperCase() }}
                </div>
<div class="d-flex align-baseline">
                  <span
                    style="
                      font-size: 56px;
                      font-weight: 700;
                      color: #333;
                      line-height: 1;
                    "
                    >{{ surveyData.goal.answeredQuestions }}</span
                  >
                  <span
                    style="font-size:56px; font-weight: 700; color: #333"
                    >/{{  surveyData.goal.noOfQuestions }}</span
                  >
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Water Scarcity Section -->
        <v-row>
          <v-col cols="12">
            <div
              class="water-scarcity-section"
              v-for="(heading, index) in normalizedResponses"
              :key="index"
            >
              <!-- Question Header -->
              <h3 class="text-h6 font-weight-bold mb-4" style="color: #424242">
                {{ index + 1 }}. &nbsp;
                {{ (heading && heading.title) || $t("esgPlatform.assessmentFarmer.carbonFallback") + ":" }}
              </h3>

              <!-- Dynamic Questions Section -->
              <div
                v-for="(question, index2) in heading &&
                heading.assessmentQuestions
                  ? heading.assessmentQuestions
                  : []"
                :key="index2"
                class="mb-4"
              >
                <v-card
                  flat
                  class="pa-6"
                  style="
                    border-radius: 0;
                    box-shadow: none;
                    border: none;
                    background: #fff;
                  "
                >
                  <!-- Question Top Row -->
                  <div class="d-flex justify-space-between align-start mb-6">
                    <div style="color: #5D5D5DE8;" class="text-caption font-weight-medium">
                      {{ $t("esgPlatform.assessmentFarmer.questionsPrefix") }} {{ index + 1 }}.{{ index2 + 1 }}
                    </div>
                    <div
                      class="text-right d-flex flex-column align-end justify-center"
                      v-if="
                        getActiveResponse(question) &&
                        (!getRepeatSubmissions(question) ||
                          !getRepeatSubmissions(question).length)
                      "
                    >
                      <div class="grey--text text-caption mb-1">
                        {{ $t("esgPlatform.assessmentFarmer.submittedOn") }}:
                      </div>
                      <div
                        class="text-caption font-weight-bold"
                        style="
                          color: #212121;
                          font-size: 13px !important;
                          line-height: 1;
                        "
                      >
                        {{
                          formatDate(
                            getActiveResponse(question).repetitionDate ||
                              question.updatedAt,
                          )
                        }}
                      </div>
                    </div>
                  </div>

                  <v-row class="ma-0">
                    <v-col
                      cols="12"
                      :md="
                        getRepeatSubmissions(question) &&
                        getRepeatSubmissions(question).length
                          ? 8
                          : 12
                      "
                      class="pa-0"
                      :class="
                        getRepeatSubmissions(question) &&
                        getRepeatSubmissions(question).length
                          ? 'pr-md-6'
                          : ''
                      "
                    >
                      <div
                        class="d-flex justify-space-between align-start mb-6"
                      >
                        <div
                          class="text-subtitle-1 font-weight-bold"
                          style="color: #212121; max-width: 70%"
                        >
                          {{ question.title }}
                        </div>
                        <div
                          class="text-right d-flex flex-column align-end justify-center"
                          v-if="
                            getActiveResponse(question) &&
                            getRepeatSubmissions(question) &&
                            getRepeatSubmissions(question).length > 0
                          "
                        >
                          <div class="grey--text text-caption mb-1">
                            {{ $t("esgPlatform.assessmentFarmer.submittedOn") }}:
                          </div>
                          <div
                            class="text-caption font-weight-bold"
                            style="
                              color: #212121;
                              font-size: 13px !important;
                              line-height: 1;
                            "
                          >
                            {{
                              formatDate(
                                getActiveResponse(question).repetitionDate ||
                                  question.updatedAt,
                              )
                            }}
                          </div>
                        </div>
                      </div>

                      <div class="answer-section pa-0" v-if="question.response">
                        <template v-for="question in getScopedQuestions(question)">
                          <div
                            :key="question.__scopeKey"
                            :class="
                              question.__isSeparateFarmScope
                                ? 'separate-farm-answer pa-4 mb-4'
                                : ''
                            "
                          >
                            <div
                              v-if="question.__isSeparateFarmScope"
                              class="d-flex align-center flex-wrap mb-4"
                              style="gap: 8px"
                            >
                              <span
                                class="text-caption grey--text text--darken-1 font-weight-bold"
                              >
                                {{ $t("esgPlatform.assessmentFarmer.farm") }}:
                              </span>
                              <v-chip
                                small
                                label
                                color="#E8F5E9"
                                text-color="#135B00"
                              >
                                {{ question.__farmName }}
                              </v-chip>
                            </div>
                            <v-divider
                              v-if="question.__isSeparateFarmScope"
                              class="mb-4"
                            />
                        <!-- Text Field & Text Area & Numeric Response -->
                        <div
                          v-if="
                            [
                              'text-field',
                              'text-area',
                              'numeric',
                              'measurement-unit',
                              'date',
                            ].includes(question.questionType)
                          "
                          class="mb-6"
                        >
                          <v-text-field
                            v-if="question.questionType === 'text-field'"
                            :value="getQuestionResponseValue(question)"
                            outlined
                            dense
                            readonly
                            hide-details
                            class="mb-2"
                          />
                          <v-textarea
                            v-if="question.questionType === 'text-area'"
                            :value="getQuestionResponseValue(question)"
                            outlined
                            auto-grow
                            rows="3"
                            readonly
                            hide-details
                            class="mb-2"
                          />
                          <v-text-field
                            v-if="question.questionType === 'numeric'"
                            :value="getQuestionResponseValue(question)"
                            type="number"
                            outlined
                            dense
                            readonly
                            hide-details
                            class="mb-2"
                          />
                          <v-text-field
                            v-if="question.questionType === 'measurement-unit'"
                            :value="getQuestionResponseValue(question)"
                            outlined
                            dense
                            readonly
                            hide-details
                            class="mb-2"
                          />
                          <v-text-field
                            v-if="question.questionType === 'date'"
                            :value="getQuestionResponseValue(question)"
                            type="date"
                            outlined
                            dense
                            readonly
                            hide-details
                            class="mb-2"
                          />

                          <div
                            v-if="
                              !getQuestionResponseValue(question) &&
                              getQuestionResponseValue(question) !== 0
                            "
                            class="caption grey--text"
                          >
                            {{ $t("esgPlatform.assessmentFarmer.noResponse") }}
                          </div>
                        </div>

                        <!-- Radio Button Response -->
                        <div
                          v-if="question.questionType === 'radio-button'"
                          class="mb-6"
                        >
                          <v-radio-group
                            :value="getSelectedRadioValue(question)"
                            readonly
                            hide-details
                            class="mt-0 pt-0"
                          >
                            <div
                              v-for="option in question.options"
                              :key="option._id || option.id"
                              class="pa-3 mb-3 d-flex align-center radio-box-item"
                            >
                              <v-radio
                                :label="option.label"
                                :value="option._id || option.id"
                                readonly
                                hide-details
                                class="mt-0 pr-2"
                              />
                            </div>
                          </v-radio-group>

                          <template v-if="getSelectedRadioOption(question)">
                            <div
                              v-if="getRadioActionPlanResponses(question).length"
                              class="ml-4 mt-4"
                            >
                              <div
                                class="font-weight-bold mb-3 text-subtitle-2"
                                style="color: #212121"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.actionPlans') }}
                              </div>
                              <div
                                v-for="plan in getRadioActionPlanResponses(question)"
                                :key="plan.id || plan.title"
                                class="action-plan-box mb-3 pa-3"
                              >
                                <div
                                  class="text-caption font-weight-bold mb-2"
                                  style="color: #212121"
                                >
                                  {{ plan.title || "-" }}
                                </div>
                                <div class="d-flex align-start flex-wrap">
                                  <div
                                    v-for="(file, fileIdx) in plan.files || []"
                                    :key="`${plan.id || plan.title}-${fileIdx}`"
                                    class="d-flex flex-column mr-4 mb-2"
                                  >
                                    <div class="file-preview-box">
                                      <button
                                        type="button"
                                        class="file-preview-trigger"
                                        @click="downloadFile(file)"
                                      >
                                        <img
                                          v-if="isImage(file)"
                                          :src="getFileUrl(file)"
                                          class="file-preview-image"
                                        />
                                        <div v-else class="pdf-file-box">
                                          <div
                                            class="text-h6 font-weight-bold mt-1"
                                          >
                                            {{ getFileType(file) }}
                                          </div>
                                        </div>
                                        <div class="file-preview-overlay">
                                          <div class="file-preview-overlay-content">
                                            <v-icon color="#135B00" small
                                              >mdi-download</v-icon
                                            >
                                            <div
                                              class="file-preview-extension text-h6 font-weight-bold"
                                            >
                                              {{ getFileType(file) }}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                    <div
                                      class="caption mt-1 text-truncate"
                                      style="max-width: 104px; color: #135b00"
                                    >
                                      <button
                                        type="button"
                                        class="file-link-button"
                                        @click="downloadFile(file)"
                                      >
                                        {{ getFileDisplayName(file) }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Get Data Questions inside Radio -->
                            <div
                              v-if="
                                getSelectedRadioOption(question)
                                  .isGetDataEnabled &&
                                getSelectedRadioOption(question)
                                  .getDataQuestions &&
                                getSelectedRadioOption(question)
                                  .getDataQuestions.length
                              "
                              class="ml-4 mt-4"
                            >
                              <div
                                class="font-weight-bold mb-3 text-subtitle-2"
                                style="color: #212121"
                              >
                                {{ $t("esgPlatform.assessmentFarmer.additionalInformationGetData") }}
                              </div>
                              <div
                                v-for="dataQ in (
                                  getSelectedRadioOption(question)
                                    .getDataQuestions || []
                                ).filter((q) => !q.deletedAt)"
                                :key="dataQ._id"
                                class="mb-3"
                              >
                                <div
                                  class="text-caption grey--text text--darken-2 font-weight-bold mb-1"
                                >
                                  {{ dataQ.title }}
                                </div>
                                <v-text-field
                                  v-if="
                                    [
                                      'text-field',
                                      'numeric',
                                      'measurement-unit',
                                      'date',
                                    ].includes(dataQ.dataType)
                                  "
                                  :value="
                                    getNestedDataResponse(question, dataQ._id)
                                  "
                                  outlined
                                  dense
                                  readonly
                                  hide-details
                                />
                                <v-textarea
                                  v-if="dataQ.dataType === 'text-area'"
                                  :value="
                                    getNestedDataResponse(question, dataQ._id)
                                  "
                                  outlined
                                  auto-grow
                                  rows="2"
                                  readonly
                                  hide-details
                                />

                                <!-- Radio / Checkbox inside get-data -->
                                <div
                                  v-if="
                                    ['radio-button', 'check-boxes'].includes(
                                      dataQ.dataType,
                                    )
                                  "
                                  class="d-flex flex-wrap mt-1"
                                >
                                  <template
                                    v-if="
                                      Array.isArray(
                                        getNestedDataResponse(
                                          question,
                                          dataQ._id,
                                        ),
                                      )
                                    "
                                  >
                                    <v-chip
                                      v-for="(
                                        chip, ci
                                      ) in getNestedDataResponse(
                                        question,
                                        dataQ._id,
                                      )"
                                      :key="ci"
                                      small
                                      class="mr-2 mb-1"
                                      color="#E8F5E9"
                                      text-color="#2E7D32"
                                      >{{ chip }}</v-chip
                                    >
                                  </template>
                                  <v-chip
                                    v-else-if="
                                      getNestedDataResponse(question, dataQ._id)
                                    "
                                    small
                                    class="mr-2 mb-1"
                                    color="#E8F5E9"
                                    text-color="#2E7D32"
                                    >{{
                                      getNestedDataResponse(question, dataQ._id)
                                    }}</v-chip
                                  >
                                </div>

                                <div
                                  v-if="
                                    [
                                      'attach-file',
                                      'digital-signature',
                                    ].includes(dataQ.dataType)
                                  "
                                  class="d-flex align-start flex-wrap mt-2"
                                >
                                  <div
                                    v-for="(file, idx) in Array.isArray(
                                      getNestedDataResponse(
                                        question,
                                        dataQ._id,
                                        true,
                                      ),
                                    )
                                      ? getNestedDataResponse(
                                          question,
                                          dataQ._id,
                                          true,
                                        )
                                      : [
                                          getNestedDataResponse(
                                            question,
                                            dataQ._id,
                                            true,
                                          ),
                                        ].filter(Boolean)"
                                    :key="idx"
                                    class="d-flex flex-column mr-4 mb-2"
                                  >
                                    <div class="file-preview-box">
                                      <button
                                        type="button"
                                        class="file-preview-trigger"
                                        @click="downloadFile(file)"
                                      >
                                        <img
                                          v-if="isImage(file)"
                                          :src="getFileUrl(file)"
                                          class="file-preview-image"
                                        />
                                        <div v-else class="pdf-file-box">
                                          <div
                                            class="text-h6 font-weight-bold mt-1"
                                          >
                                            {{ getFileType(file) }}
                                          </div>
                                        </div>
                                        <div class="file-preview-overlay">
                                          <div class="file-preview-overlay-content">
                                            <v-icon color="#135B00" small
                                              >mdi-download</v-icon
                                            >
                                            <div
                                              class="file-preview-extension text-h6 font-weight-bold"
                                            >
                                              {{ getFileType(file) }}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                    <div
                                      class="caption mt-1 text-truncate"
                                      style="max-width: 104px; color: #135b00"
                                    >
                                      <button
                                        type="button"
                                        class="file-link-button"
                                        @click="downloadFile(file)"
                                      >
                                        {{ getFileDisplayName(file) }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              v-if="
                                getSelectedRadioOption(question)
                                  .isDimitraModulesEnabled &&
                                hasDimitraModuleRows(
                                  getRadioDimitraModuleEntries(question),
                                )
                              "
                              class="ml-4 mt-4"
                            >
                              <div
                                class="font-weight-bold mb-3 text-subtitle-2"
                                style="color: #212121"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.dimitraModule') }}
                              </div>
                              <div
                                v-for="(rows, key) in getRadioDimitraModuleEntries(
                                  question,
                                )"
                                :key="`radio-dimitra-${key}`"
                                class="mb-4"
                              >
                                <div
                                  class="text-caption font-weight-bold mb-3 dimitra-module-heading"
                                >
                                  Dimitra {{ getDimitraModuleDisplayName(key) }}
                                </div>
                                <v-row class="ma-n2">
                                  <v-col
                                    v-for="(row, rowIdx) in rows"
                                    :key="`radio-dimitra-${key}-${row.id || rowIdx}`"
                                    cols="12"
                                    sm="6"
                                    lg="4"
                                    class="pa-2"
                                  >
                                    <v-card
                                      flat
                                      class="dimitra-summary-card d-flex flex-column pa-4"
                                    >
                                      <div class="dimitra-summary-title mb-4">
                                        {{ getDimitraCardTitle(key, row, rowIdx) }}
                                      </div>

                                      <div
                                        v-if="getDimitraPreviewFields(key, row, rowIdx).length || shouldShowFarmGeofenceLink(key, row)"
                                        class="dimitra-summary-grid"
                                      >
                                        <div
                                          v-for="field in getDimitraPreviewFields(
                                            key,
                                            row,
                                            rowIdx,
                                          )"
                                          :key="`${key}-${row.id || rowIdx}-${field.key}`"
                                          class="dimitra-summary-field"
                                        >
                                          <div class="dimitra-summary-label">
                                            {{ getTranslatedDimitraFieldLabel(field) }}
                                          </div>
                                          <div class="dimitra-summary-value">
                                            {{ field.value }}
                                          </div>
                                        </div>
                                        <div
                                          v-if="shouldShowFarmGeofenceLink(key, row)"
                                          class="dimitra-summary-field dimitra-summary-field--full"
                                        >
                                          <div class="dimitra-summary-label">{{ $t('esgPlatform.assessmentFarmer.farmGeofenceLabel') }}</div>
                                          <div class="dimitra-summary-value">
                                            <div class="dimitra-geo-chip">{{ $t('esgPlatform.assessmentFarmer.polygon') }}</div>
                                            <button
                                              type="button"
                                              class="action-link-button dimitra-map-link"
                                              @click="openFarmGeofenceDialog(key, row, rowIdx)"
                                            >
                                              {{ $t('esgPlatform.assessmentFarmer.viewOnMap') }}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        v-else
                                        class="dimitra-summary-empty mb-4"
                                      >
                                        {{ $t('esgPlatform.assessmentFarmer.noDetailsAvailable') }}
                                      </div>

                                      <div v-if="key === 'Farms' && row.deforestationStatus && row.deforestationStatus.deforestationStatus" class="deforestation-prob text-center mt-2 mb-2">
                                        <v-divider class="mb-4" />
                                        <div class="text-caption font-weight-bold mb-3">{{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') }}</div>
                                        <div class="status-pill mb-4 mx-auto"
                                        :class="getStatusClass(row.deforestationStatus.deforestationStatus)"
                                        >
                                          {{ row.deforestationStatus.deforestationStatus }}
                                        </div>
                                        <div class="text-caption mb-1" style="color: #616161;">{{ $t('esgPlatform.assessmentFarmer.reportDate') }}: <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(row.deforestationStatus.created_at) }}</span></div>
                                        <button type="button" class="action-link-button dimitra-map-link mt-1" @click.stop="goToDeforestationReport(row.deforestationStatus.id)">
                                          {{ $t('esgPlatform.assessmentFarmer.viewReport') }}
                                        </button>
                                      </div>

                                      <div
                                        v-if="
                                          getDimitraHiddenFieldCount(
                                            key,
                                            row,
                                            rowIdx,
                                          ) > 0
                                        "
                                        class="dimitra-summary-more mt-4"
                                      >
                                        {{ $t('esgPlatform.assessmentFarmer.moreDetailsAvailable', { count: getDimitraHiddenFieldCount(key, row, rowIdx) }) }}
                                      </div>

                                      <div class="mt-auto pt-4">
                                        <v-btn
                                          v-if="
                                            shouldShowDimitraDetailsButton(
                                              key,
                                              row,
                                              rowIdx,
                                            )
                                          "
                                          outlined
                                          class="dimitra-detail-btn"
                                          @click="
                                            openDimitraDetailsDialog(
                                              key,
                                              row,
                                              rowIdx,
                                            )
                                          "
                                        >
                                          {{ $t('esgPlatform.viewDetails') }}
                                        </v-btn>
                                      </div>
                                    </v-card>
                                  </v-col>
                                </v-row>
                              </div>
                            </div>
                          </template>
                        </div>

                        <!-- Checkbox Response -->
                        <div
                          v-if="question.questionType === 'check-boxes'"
                          class="mb-6"
                        >
                          <div
                            v-for="option in question.options"
                            :key="option._id || option.id"
                            class="pa-3 mb-3 radio-box-item"
                          >
                            <v-checkbox
                              :input-value="isOptionSelected(question, option)"
                              :label="option.label"
                              readonly
                              hide-details
                              class="mt-0 pt-0"
                            />

                            <!-- Get Data Questions inside Checkbox mapping -->
                            <div
                              v-if="
                                isOptionSelected(question, option) &&
                                getCheckboxActionPlanResponses(question, option)
                                  .length
                              "
                              class="ml-8 mt-4 mb-2"
                            >
                              <div
                                class="font-weight-bold mb-2 text-caption"
                                style="color: #212121"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.actionPlans') }}
                              </div>
                              <div
                                v-for="plan in getCheckboxActionPlanResponses(
                                  question,
                                  option,
                                )"
                                :key="plan.id || plan.title"
                                class="action-plan-box mb-3 pa-3"
                              >
                                <div
                                  class="text-caption font-weight-bold mb-2"
                                  style="color: #212121"
                                >
                                  {{ plan.title || "-" }}
                                </div>
                                <div class="d-flex align-start flex-wrap">
                                  <div
                                    v-for="(file, fileIdx) in plan.files || []"
                                    :key="`${plan.id || plan.title}-${fileIdx}`"
                                    class="d-flex flex-column mr-4 mb-2"
                                  >
                                    <div class="file-preview-box">
                                      <button
                                        type="button"
                                        class="file-preview-trigger"
                                        @click="downloadFile(file)"
                                      >
                                        <img
                                          v-if="isImage(file)"
                                          :src="getFileUrl(file)"
                                          class="file-preview-image"
                                        />
                                        <div v-else class="pdf-file-box">
                                          <div
                                            class="text-h6 font-weight-bold mt-1"
                                          >
                                            {{ getFileType(file) }}
                                          </div>
                                        </div>
                                        <div class="file-preview-overlay">
                                          <div class="file-preview-overlay-content">
                                            <v-icon color="#135B00" small
                                              >mdi-download</v-icon
                                            >
                                            <div
                                              class="file-preview-extension text-h6 font-weight-bold"
                                            >
                                              {{ getFileType(file) }}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                    <div
                                      class="caption mt-1 text-truncate"
                                      style="max-width: 104px; color: #135b00"
                                    >
                                      <button
                                        type="button"
                                        class="file-link-button"
                                        @click="downloadFile(file)"
                                      >
                                        {{ getFileDisplayName(file) }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              v-if="
                                isOptionSelected(question, option) &&
                                option.isGetDataEnabled &&
                                option.getDataQuestions &&
                                option.getDataQuestions.length
                              "
                              class="ml-8 mt-4 mb-2"
                            >
                              <div
                                class="font-weight-bold mb-2 text-caption"
                                style="color: #212121"
                              >
                                Additional Information
                              </div>
                              <div
                                v-for="dataQ in (
                                  option.getDataQuestions || []
                                ).filter((q) => !q.deletedAt)"
                                :key="dataQ._id"
                                class="mb-3"
                              >
                                <div
                                  class="text-caption grey--text text--darken-2 font-weight-bold mb-1"
                                >
                                  {{ dataQ.title }}
                                </div>
                                <v-text-field
                                  v-if="
                                    [
                                      'text-field',
                                      'numeric',
                                      'measurement-unit',
                                      'date',
                                    ].includes(dataQ.dataType)
                                  "
                                  :value="
                                    getCheckboxNestedDataResponse(
                                      question,
                                      option,
                                      dataQ._id,
                                    )
                                  "
                                  outlined
                                  dense
                                  readonly
                                  hide-details
                                />
                                <v-textarea
                                  v-if="dataQ.dataType === 'text-area'"
                                  :value="
                                    getCheckboxNestedDataResponse(
                                      question,
                                      option,
                                      dataQ._id,
                                    )
                                  "
                                  outlined
                                  auto-grow
                                  rows="2"
                                  readonly
                                  hide-details
                                />

                                <!-- Radio / Checkbox inside checkbox get-data -->
                                <div
                                  v-if="
                                    ['radio-button', 'check-boxes'].includes(
                                      dataQ.dataType,
                                    )
                                  "
                                  class="d-flex flex-wrap mt-1"
                                >
                                  <template
                                    v-if="
                                      Array.isArray(
                                        getCheckboxNestedDataResponse(
                                          question,
                                          option,
                                          dataQ._id,
                                        ),
                                      )
                                    "
                                  >
                                    <v-chip
                                      v-for="(
                                        chip, ci
                                      ) in getCheckboxNestedDataResponse(
                                        question,
                                        option,
                                        dataQ._id,
                                      )"
                                      :key="ci"
                                      small
                                      class="mr-2 mb-1"
                                      color="#E8F5E9"
                                      text-color="#2E7D32"
                                      >{{ chip }}</v-chip
                                    >
                                  </template>
                                  <v-chip
                                    v-else-if="
                                      getCheckboxNestedDataResponse(
                                        question,
                                        option,
                                        dataQ._id,
                                      )
                                    "
                                    small
                                    class="mr-2 mb-1"
                                    color="#E8F5E9"
                                    text-color="#2E7D32"
                                    >{{
                                      getCheckboxNestedDataResponse(
                                        question,
                                        option,
                                        dataQ._id,
                                      )
                                    }}</v-chip
                                  >
                                </div>

                                <div
                                  v-if="
                                    [
                                      'attach-file',
                                      'digital-signature',
                                    ].includes(dataQ.dataType)
                                  "
                                  class="d-flex align-start flex-wrap mt-2"
                                >
                                  <div
                                    v-for="(file, idx) in Array.isArray(
                                      getCheckboxNestedDataResponse(
                                        question,
                                        option,
                                        dataQ._id,
                                        true,
                                      ),
                                    )
                                      ? getCheckboxNestedDataResponse(
                                          question,
                                          option,
                                          dataQ._id,
                                          true,
                                        )
                                      : [
                                          getCheckboxNestedDataResponse(
                                            question,
                                            option,
                                            dataQ._id,
                                            true,
                                          ),
                                        ].filter(Boolean)"
                                    :key="idx"
                                    class="d-flex flex-column mr-4"
                                  >
                                    <div class="file-preview-box">
                                      <button
                                        type="button"
                                        class="file-preview-trigger"
                                        @click="downloadFile(file)"
                                      >
                                        <img
                                          v-if="isImage(file)"
                                          :src="getFileUrl(file)"
                                          class="file-preview-image"
                                        />
                                        <div v-else class="pdf-file-box">
                                          <div
                                            class="text-h6 font-weight-bold mt-1"
                                          >
                                            {{ getFileType(file) }}
                                          </div>
                                        </div>
                                        <div class="file-preview-overlay">
                                          <div class="file-preview-overlay-content">
                                            <v-icon color="#135B00" small
                                              >mdi-download</v-icon
                                            >
                                            <div
                                              class="file-preview-extension text-h6 font-weight-bold"
                                            >
                                              {{ getFileType(file) }}
                                            </div>
                                          </div>
                                        </div>
                                      </button>
                                    </div>
                                    <div
                                      class="caption mt-1 text-truncate"
                                      style="max-width: 104px; color: #135b00"
                                    >
                                      <button
                                        type="button"
                                        class="file-link-button"
                                        @click="downloadFile(file)"
                                      >
                                        {{ getFileDisplayName(file) }}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div
                              v-if="
                                isOptionSelected(question, option) &&
                                option.isDimitraModulesEnabled &&
                                hasDimitraModuleRows(
                                  getCheckboxDimitraModuleEntries(
                                    question,
                                    option,
                                  ),
                                )
                              "
                              class="ml-8 mt-4 mb-2"
                            >
                              <div
                                class="font-weight-bold mb-2 text-caption"
                                style="color: #212121"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.dimitraModule') }}
                              </div>
                              <div
                                v-for="(rows, key) in getCheckboxDimitraModuleEntries(
                                  question,
                                  option,
                                )"
                                :key="`checkbox-dimitra-${option._id || option.id}-${key}`"
                                class="mb-4"
                              >
                                <div
                                  class="text-caption font-weight-bold mb-3 dimitra-module-heading"
                                >
                                  Dimitra {{ getDimitraModuleDisplayName(key) }}
                                </div>
                                <v-row class="ma-n2">
                                  <v-col
                                    v-for="(row, rowIdx) in rows"
                                    :key="`checkbox-dimitra-${option._id || option.id}-${key}-${row.id || rowIdx}`"
                                    cols="12"
                                    sm="6"
                                    lg="4"
                                    class="pa-2"
                                  >
                                    <v-card
                                      flat
                                      class="dimitra-summary-card d-flex flex-column pa-4"
                                    >
                                      <div class="dimitra-summary-title mb-4">
                                        {{ getDimitraCardTitle(key, row, rowIdx) }}
                                      </div>

                                      <div
                                        v-if="getDimitraPreviewFields(key, row, rowIdx).length || shouldShowFarmGeofenceLink(key, row)"
                                        class="dimitra-summary-grid"
                                      >
                                        <div
                                          v-for="field in getDimitraPreviewFields(
                                            key,
                                            row,
                                            rowIdx,
                                          )"
                                          :key="`${key}-${row.id || rowIdx}-${field.key}`"
                                          class="dimitra-summary-field"
                                        >
                                          <div class="dimitra-summary-label">
                                            {{ getTranslatedDimitraFieldLabel(field) }}
                                          </div>
                                          <div class="dimitra-summary-value">
                                            {{ field.value }}
                                          </div>
                                        </div>
                                        <div
                                          v-if="shouldShowFarmGeofenceLink(key, row)"
                                          class="dimitra-summary-field dimitra-summary-field--full"
                                        >
                                          <div class="dimitra-summary-label">{{ $t('esgPlatform.assessmentFarmer.farmGeofenceLabel') }}</div>
                                          <div class="dimitra-summary-value">
                                            <div class="dimitra-geo-chip">{{ $t('esgPlatform.assessmentFarmer.polygon') }}</div>
                                            <button
                                              type="button"
                                              class="action-link-button dimitra-map-link"
                                              @click="openFarmGeofenceDialog(key, row, rowIdx)"
                                            >
                                              {{ $t('esgPlatform.assessmentFarmer.viewOnMap') }}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        v-else
                                        class="dimitra-summary-empty mb-4"
                                      >
                                        {{ $t('esgPlatform.assessmentFarmer.noDetailsAvailable') }}
                                      </div>

                                      <div v-if="key === 'Farms' && row.deforestationStatus && row.deforestationStatus.deforestationStatus" class="deforestation-prob text-center mt-2 mb-2">
                                        <v-divider class="mb-4" />
                                        <div class="text-caption font-weight-bold mb-3">{{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') }}</div>
                                        <div class="status-pill mb-4 mx-auto"
                                        :class="getStatusClass(row.deforestationStatus.deforestationStatus)"
                                        >
                                          {{ row.deforestationStatus.deforestationStatus }}
                                        </div>
                                        <div class="text-caption mb-1" style="color: #616161;">{{ $t('esgPlatform.assessmentFarmer.reportDate') }}: <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(row.deforestationStatus.created_at) }}</span></div>
                                        <button type="button" class="action-link-button dimitra-map-link mt-1" @click.stop="openDimitraDetailsDialog(key, row, rowIdx)">
                                          {{ $t('esgPlatform.assessmentFarmer.viewReport') }}
                                        </button>
                                      </div>

                                      <div
                                        v-if="
                                          getDimitraHiddenFieldCount(
                                            key,
                                            row,
                                            rowIdx,
                                          ) > 0
                                        "
                                        class="dimitra-summary-more mt-4"
                                      >
                                        {{ $t('esgPlatform.assessmentFarmer.moreDetailsAvailable', { count: getDimitraHiddenFieldCount(key, row, rowIdx) }) }}
                                      </div>

                                      <div class="mt-auto pt-4">
                                        <v-btn
                                          v-if="
                                            shouldShowDimitraDetailsButton(
                                              key,
                                              row,
                                              rowIdx,
                                            )
                                          "
                                          outlined
                                          class="dimitra-detail-btn"
                                          @click="
                                            openDimitraDetailsDialog(
                                              key,
                                              row,
                                              rowIdx,
                                            )
                                          "
                                        >
                                          {{ $t('esgPlatform.viewDetails') }}
                                        </v-btn>
                                      </div>
                                    </v-card>
                                  </v-col>
                                </v-row>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Dimitra Module Response -->
                        <div
                          v-if="question.questionType === 'dimitra-module'"
                          class="mb-6"
                        >
                          <div
                            v-for="(rows, key) in getDimitraModuleEntries(
                              question,
                            )"
                            :key="key"
                            class="mb-4"
                          >
                            <div
                              class="text-caption font-weight-bold mb-3 dimitra-module-heading"
                            >
                                Dimitra {{getDimitraModuleDisplayName(key)}}
                            </div>
                            <v-row class="ma-n2">
                              <v-col
                                v-for="(row, rowIdx) in rows"
                                :key="`root-dimitra-${key}-${row.id || rowIdx}`"
                                cols="12"
                                sm="6"
                                lg="4"
                                class="pa-2"
                              >
                                <v-card
                                  flat
                                  class="dimitra-summary-card d-flex flex-column pa-4"
                                >
                                  <div class="dimitra-summary-title mb-4">
                                    {{ getDimitraCardTitle(key, row, rowIdx) }}
                                  </div>

                                  <div
                                    v-if="getDimitraPreviewFields(key, row, rowIdx).length || shouldShowFarmGeofenceLink(key, row)"
                                    class="dimitra-summary-grid"
                                  >
                                    <div
                                      v-for="field in getDimitraPreviewFields(
                                        key,
                                        row,
                                        rowIdx,
                                      )"
                                      :key="`${key}-${row.id || rowIdx}-${field.key}`"
                                      class="dimitra-summary-field"
                                    >
                                      <div class="dimitra-summary-label">
                                        {{ getTranslatedDimitraFieldLabel(field) }}
                                      </div>
                                      <div class="dimitra-summary-value">
                                        {{ field.value }}
                                      </div>
                                    </div>
                                    <div
                                      v-if="shouldShowFarmGeofenceLink(key, row)"
                                      class="dimitra-summary-field dimitra-summary-field--full"
                                    >
                                      <div class="dimitra-summary-label">{{ $t('esgPlatform.assessmentFarmer.farmGeofenceLabel') }}</div>
                                      <div class="dimitra-summary-value">
                                        <div class="dimitra-geo-chip">{{ $t('esgPlatform.assessmentFarmer.polygon') }}</div>
                                        <button
                                          type="button"
                                          class="action-link-button dimitra-map-link"
                                          @click="openFarmGeofenceDialog(key, row, rowIdx)"
                                        >
                                          {{ $t('esgPlatform.assessmentFarmer.viewOnMap') }}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div
                                    v-else
                                    class="dimitra-summary-empty mb-4"
                                  >
                                    {{ $t('esgPlatform.assessmentFarmer.noDetailsAvailable') }}
                                  </div>

                                  <div v-if="key === 'Farms' && row.deforestationStatus && row.deforestationStatus.deforestationStatus" class="deforestation-prob text-center mt-2 mb-2">
                                    <v-divider class="mb-4" />
                                    <div class="text-caption font-weight-bold mb-3">{{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') }}</div>
                                    <div class="status-pill mb-4 mx-auto"
                                    :class="getStatusClass(row.deforestationStatus.deforestationStatus)"
                                    >
                                      {{ row.deforestationStatus.deforestationStatus }}
                                    </div>
                                    <div class="text-caption mb-1" style="color: #616161;">{{ $t('esgPlatform.assessmentFarmer.reportDate') }}: <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(row.deforestationStatus.created_at) }}</span></div>
                                    <button type="button" class="action-link-button dimitra-map-link mt-1" @click.stop="goToDeforestationReport(row.deforestationStatus.id)">
                                      {{ $t('esgPlatform.assessmentFarmer.viewReport') }}
                                    </button>
                                  </div>

                                  <div
                                    v-if="
                                      getDimitraHiddenFieldCount(
                                        key,
                                        row,
                                        rowIdx,
                                      ) > 0
                                    "
                                    class="dimitra-summary-more mt-4"
                                  >
                                    {{ $t('esgPlatform.assessmentFarmer.moreDetailsAvailable', { count: getDimitraHiddenFieldCount(key, row, rowIdx) }) }}
                                  </div>

                                  <div class="mt-auto pt-4">
                                    <v-btn
                                      v-if="
                                        shouldShowDimitraDetailsButton(
                                          key,
                                          row,
                                          rowIdx,
                                        )
                                      "
                                      outlined
                                      class="dimitra-detail-btn"
                                      @click="
                                        openDimitraDetailsDialog(
                                          key,
                                          row,
                                          rowIdx,
                                        )
                                      "
                                    >
                                      {{ $t('esgPlatform.viewDetails') }}
                                    </v-btn>
                                  </div>
                                </v-card>
                              </v-col>
                            </v-row>
                          </div>
                        </div>

                        <!-- Provide Additional Data (Root level get-data) -->
                        <div
                          v-if="
                            question.questionType === 'get-data' &&
                            question.getDataQuestions &&
                            question.getDataQuestions.length
                          "
                          class="mb-6"
                        >
                          <div
                            v-for="dataQ in (
                              question.getDataQuestions || []
                            ).filter((q) => !q.deletedAt)"
                            :key="dataQ._id"
                            class="mb-4"
                          >
                            <div
                              class="text-caption grey--text text--darken-2 font-weight-bold mb-1"
                            >
                              {{ dataQ.title }}
                            </div>
                            <v-text-field
                              v-if="
                                [
                                  'text-field',
                                  'numeric',
                                  'measurement-unit',
                                  'date',
                                ].includes(dataQ.dataType)
                              "
                              :value="
                                getRootGetDataResponse(question, dataQ._id)
                              "
                              outlined
                              dense
                              readonly
                              hide-details
                            />
                            <v-textarea
                              v-if="dataQ.dataType === 'text-area'"
                              :value="
                                getRootGetDataResponse(question, dataQ._id)
                              "
                              outlined
                              auto-grow
                              rows="2"
                              readonly
                              hide-details
                            />

                            <!-- Radio / Checkbox inside root get-data -->
                            <div
                              v-if="
                                ['radio-button', 'check-boxes'].includes(
                                  dataQ.dataType,
                                )
                              "
                              class="d-flex flex-wrap mt-1"
                            >
                              <template
                                v-if="
                                  Array.isArray(
                                    getRootGetDataResponse(question, dataQ._id),
                                  )
                                "
                              >
                                <v-chip
                                  v-for="(chip, ci) in getRootGetDataResponse(
                                    question,
                                    dataQ._id,
                                  )"
                                  :key="ci"
                                  small
                                  class="mr-2 mb-1"
                                  color="#E8F5E9"
                                  text-color="#2E7D32"
                                  >{{ chip }}</v-chip
                                >
                              </template>
                              <v-chip
                                v-else-if="
                                  getRootGetDataResponse(question, dataQ._id)
                                "
                                small
                                class="mr-2 mb-1"
                                color="#E8F5E9"
                                text-color="#2E7D32"
                                >{{
                                  getRootGetDataResponse(question, dataQ._id)
                                }}</v-chip
                              >
                            </div>

                            <div
                              v-if="
                                ['attach-file', 'digital-signature'].includes(
                                  dataQ.dataType,
                                )
                              "
                              class="d-flex align-start flex-wrap mt-2"
                            >
                              <div
                                v-for="(file, idx) in Array.isArray(
                                  getRootGetDataResponse(
                                    question,
                                    dataQ._id,
                                    true,
                                  ),
                                )
                                  ? getRootGetDataResponse(
                                      question,
                                      dataQ._id,
                                      true,
                                    )
                                  : [
                                      getRootGetDataResponse(
                                        question,
                                        dataQ._id,
                                        true,
                                      ),
                                    ].filter(Boolean)"
                                :key="'rootfile-' + idx"
                                class="d-flex flex-column mr-4"
                              >
                                <div class="file-preview-box">
                                  <button
                                    type="button"
                                    class="file-preview-trigger"
                                    @click="downloadFile(file)"
                                  >
                                    <img
                                      v-if="isImage(file)"
                                      :src="getFileUrl(file)"
                                      class="file-preview-image"
                                    />
                                    <div v-else class="pdf-file-box">
                                      <div
                                        class="text-h6 font-weight-bold mt-1"
                                      >
                                        {{ getFileType(file) }}
                                      </div>
                                    </div>
                                    <div class="file-preview-overlay">
                                      <div class="file-preview-overlay-content">
                                        <v-icon color="#135B00" small
                                          >mdi-download</v-icon
                                        >
                                        <div
                                          class="file-preview-extension text-h6 font-weight-bold"
                                        >
                                          {{ getFileType(file) }}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                </div>
                                <div
                                  class="caption mt-1 text-truncate"
                                  style="max-width: 104px; color: #135b00"
                                >
                                  <button
                                    type="button"
                                    class="file-link-button"
                                    @click="downloadFile(file)"
                                  >
                                    {{ getFileDisplayName(file) }}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Attach Files & Digital Signature -->
                        <div
                          v-if="
                            ['attach-files', 'digital-signature'].includes(
                              question.questionType,
                            )
                          "
                          class="mb-6"
                        >
                          <div
                            class="font-weight-bold text-caption mb-3"
                            style="color: #212121"
                          >
                            Uploaded Files
                          </div>
                          <div class="d-flex align-start flex-wrap">
                            <div
                              v-for="(
                                file, idx
                              ) in extractUploadedFilesFromResponse(question)"
                              :key="idx"
                              class="d-flex mr-6 mb-4 align-start"
                            >
                              <div class="d-flex flex-column mr-4">
                                <div class="file-preview-box">
                                  <button
                                    type="button"
                                    class="file-preview-trigger"
                                    @click="downloadFile(file)"
                                  >
                                    <img
                                      v-if="isImage(file)"
                                      :src="getFileUrl(file)"
                                      class="file-preview-image"
                                    />
                                    <div v-else class="pdf-file-box">
                                      <div
                                        class="text-h6 font-weight-bold mt-1"
                                      >
                                        {{ getFileType(file) }}
                                      </div>
                                    </div>
                                    <div class="file-preview-overlay">
                                      <div class="file-preview-overlay-content">
                                        <v-icon color="#135B00" small
                                          >mdi-download</v-icon
                                        >
                                        <div
                                          class="file-preview-extension text-h6 font-weight-bold"
                                        >
                                          {{ getFileType(file) }}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                </div>
                                <div
                                  class="caption mt-2 text-truncate"
                                  style="max-width: 104px; color: #135b00"
                                >
                                  <button
                                    type="button"
                                    class="file-link-button"
                                    @click="downloadFile(file)"
                                  >
                                    {{ getFileDisplayName(file, "Document") }}
                                  </button>
                                </div>
                              </div>
                              <div
                                class="comment-section"
                                v-if="file.comments || file.comment"
                              >
                                <div
                                  class="font-weight-bold mb-1 text-caption"
                                  style="color: #212121"
                                >
                                  Comments
                                </div>
                                <p
                                  class="text-caption grey--text text--darken-2 mb-1"
                                  style="max-width: 380px; line-height: 1.4"
                                >
                                  {{
                                    (file.comments && file.comments.text) ||
                                    file.comment
                                  }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Recommendations -->
                        <div
                          v-if="getRecommendationsList(question).length"
                          class="recommendations-box mb-2 mt-6 pa-4"
                        >
                          <div
                            class="font-weight-bold mb-2 text-body-2"
                            style="color: #212121"
                          >
                            Recommendations
                          </div>
                          <ul
                            class="pl-4 text-caption"
                            style="line-height: 1.5; color: #212121"
                          >
                            <li
                              v-for="(rec, idx) in getRecommendationsList(
                                question,
                              )"
                              :key="idx"
                            >
                              {{ rec }}
                            </li>
                          </ul>
                        </div>
                            <!-- Rejection/Approval Reason -->
                            <div
                              v-if="question.rejectionReason"
                              class="rejection-block"
                            >
                              <div class="rejection-title">{{ $t('esgPlatform.surveyResponse.submissionRejected') }}</div>
                              <span class="font-weight-bold">{{ $t('esgPlatform.surveyResponse.rejectionReason') }}:</span>
                              <div class="rejection-reason">{{ question.rejectionReason }}</div>
                            </div>
                          </div>
                        </template>
                      </div>
                      <div v-else>
                        <div class="caption grey--text">No Response</div>
                      </div>

                    </v-col>

                    <!-- Repeat Submission Box Layout -->
                    <v-col
                      cols="12"
                      md="4"
                      class="pa-0 pl-md-6"
                      v-if="
                        getRepeatSubmissions(question) &&
                        getRepeatSubmissions(question).length
                      "
                    >
                      <div
                        class="h-100 pl-6"
                        style="border-left: 1px solid #e0e0e0"
                      >
                        <div
                          class="font-weight-bold mb-5"
                          style="font-size: 16px; color: #00bd73"
                        >
                          {{ $t('esgPlatform.assessmentFarmer.repeatSubmission') }}
                        </div>
                        <div class="repeat-submission-list">
                          <div
                            class="repeat-box-item d-flex align-center pa-4 mb-4"
                            v-for="(sub, sIdx) in getRepeatSubmissions(
                              question,
                            )"
                            :key="sIdx"
                            :class="{ active: sub.isSelected }"
                            :style="
                              (sub.status === 'active' || sub.status === 'rejected')
                                ? 'cursor: pointer;'
                                : 'opacity: 0.7;'
                            "
                            @click="
                              (sub.status === 'active' || sub.status === 'rejected')
                                ? setActiveSubmission(question, sub.rawDate)
                                : null
                            "
                          >
                            <div class="repeat-icon-wrapper mr-4">
                              <v-icon
                                :color="
                                  sub.isSelected
                                    ? '#00BD73'
                                    : (sub.status === 'active' || sub.status === 'rejected')
                                    ? '#00BD73'
                                    : 'grey'
                                "
                                size="24"
                              >
                                {{
                                  sub.isSelected
                                    ? "mdi-radiobox-marked"
                                    : "mdi-radiobox-blank"
                                }}
                              </v-icon>
                            </div>
                            <div class="repeat-content">
                              <div
                                class="text-caption mb-1"
                                style="
                                  color: #616161;
                                  font-size: 13px !important;
                                "
                              >
                                {{ sub.title }}
                              </div>
                              <div
                                class="text-caption font-weight-bold mb-1"
                                style="
                                  color: #212121;
                                  font-size: 13px !important;
                                "
                                v-if="sub.date"
                              >
                                {{ sub.date }}
                              </div>
                              <div
                                class="text-caption font-weight-bold"
                                style="
                                  color: #9e9e9e;
                                  font-size: 13px !important;
                                "
                                v-if="sub.status === 'upcoming'"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.upcoming') }}
                              </div>
                              <div
                                class="text-caption font-weight-bold"
                                style="color: #f57c00; font-size: 13px !important;"
                                v-if="sub.status === 'not-yet'"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.notYetSubmitted') }}
                              </div>
                              <div
                                class="text-caption font-weight-bold"
                                style="color: #9e9e9e; font-size: 13px !important;"
                                v-if="sub.status === 'not-submitted'"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.notSubmitted') }}
                              </div>
                              <div
                                class="text-caption font-weight-bold"
                                style="color: #ff5252; font-size: 13px !important;"
                                v-if="sub.status === 'rejected'"
                              >
                                {{ $t('esgPlatform.assessmentFarmer.responseRejected') }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <v-dialog
        v-model="farmGeofenceDialog"
        max-width="980"
        scrollable
      >
        <v-card class="dimitra-detail-dialog">
          <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dimitra-detail-dialog-header">
            <div class="pr-4">
              <div class="dimitra-detail-overline mb-2">
                Dimitra {{ farmGeofenceDetails.moduleName || "Farms" }}
              </div>
              <div class="dimitra-detail-title">
                {{ farmGeofenceDetails.title || "Farm Geofence" }}
              </div>
            </div>
            <v-btn icon @click="closeFarmGeofenceDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="px-6 pt-5 pb-2">
            <div
              ref="farmGeofenceMap"
              class="farm-geofence-map"
            ></div>
          </v-card-text>

          <v-card-actions class="px-6 pb-6 pt-2">
            <v-spacer />
            <v-btn
              outlined
              class="dimitra-detail-btn"
              @click="closeFarmGeofenceDialog"
            >
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import EsgService from "@/_services/EsgService";
import { DIMITRA_CONFIG_TEMPLATE } from "@/views/esg-platform/survey-builder/constants/esgDimitraModule";
import { getGoogleMapsLoader } from "@/mixins/GoogleMapLoaderSingleton";

export default {
  name: "assessmentFarmerResponses",
  data() {
    return {
      loading: false,
      assessmentId: this.$route.params.assessmentId,
      goalId: this.$route.params.goalId,
      submittedById: this.$route.params.submittedById,
      questionId: this.$route.query.questionId,
      protocolId: this.$route.query.protocolId,
      organizationId: this.$route.query.organizationId,
      surveyData: {
        goal: {},
        responses: [],
        question: null,
        submittedUser: null,
      },
      farmGeofenceDialog: false,
      farmGeofenceDetails: {
        moduleName: "",
        title: "",
        coordinates: [],
      },
      farmGeofenceMap: null,
      farmGeofencePolygon: null,
      google: null,
      breadcrumbs: [
        {
          text: this.$t("esgPlatform.esgCompliance"),
          link: true,
          exact: true,
          disabled: true,
        },
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "eudrDashboard" },
        },
      ],
    };
  },
  computed: {
    normalizedResponses() {
      if (Array.isArray(this.surveyData?.responses) && this.surveyData.responses.length) {
        return this.surveyData.responses.filter(
  (r) => Array.isArray(r.assessmentQuestions) && r.assessmentQuestions.length
);
      }

      if (this.surveyData?.question) {
        return [
          {
            _id: this.surveyData.question._id,
            title: this.surveyData.question.title || "Question",
            assessmentQuestions: [this.surveyData.question],
          },
        ];
      }

      return [];
    },
    getCompanyName() {
      return (
        this.surveyData?.submittedUser?.subOrganization?.name ||
        this.surveyData?.submittedUser?.company?.name ||
        ""
      );
    },
    getSubmittedBy() {
      return this.surveyData?.submittedUser?.firstName ||
        this.surveyData?.submittedUser?.lastName
        ? `${this.surveyData?.submittedUser?.firstName || ""} ${
            this.surveyData?.submittedUser?.lastName || ""
          }`
        : this.surveyData?.submittedUser?.email;
    },
    getFlatQuestions() {
      return (
        this.normalizedResponses?.flatMap(
          (x) => x?.assessmentQuestions || [],
        ) || []
      );
    },
    getLatestSubmittedDate() {
      const d = [...this.getFlatQuestions]
        .map((question) => {
          const activeResponse = this.getActiveResponse(question);
          return activeResponse?.repetitionDate || activeResponse?.createdAt || null;
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a))[0] || null;

      if (!d) return "-";
      return moment(d).format("DD-MMM-YYYY | hh:mm A").toUpperCase();
    },
    responseStats() {
      return {
        completed:
          this.getFlatQuestions?.filter((x) => x.response)?.length || 0,
        total: this.getFlatQuestions?.length || 0,
      };
    },
  },

  mounted() {
    this.getSurveyResponseByGoalId();
  },

    watch: {
      farmGeofenceDialog(isOpen) {
        if (isOpen) {
          this.$nextTick(() => {
            this.renderFarmGeofenceMap();
          });
        } else {
          this.teardownFarmGeofenceMap();
        }
      },
    },

  methods: {
    setActiveSubmission(question, rawDate) {
      this.$set(question, "selectedDate", rawDate);
    },
    getActiveResponse(question) {
      if (Object.prototype.hasOwnProperty.call(question || {}, "__responseItem")) {
        return question?.__responseItem || null;
      }

      if (!question.response || !question.response.length) return null;
      if (question.selectedDate) {
        const match = question.response.find(
          (r) => r.repetitionDate === question.selectedDate,
        );
        if (match) return match;
      }
      return question.response[question.response.length - 1];
    },
    getVal(question) {
      if (Object.prototype.hasOwnProperty.call(question || {}, "__responseItem")) {
        const responseItem = question?.__responseItem;
        if (!responseItem) return null;
        return (
          responseItem.assessmentResponse ||
          responseItem.response ||
          responseItem.value ||
          null
        );
      }

      if (!question.response) return null;

      // Check if it's a repeat submission question wrapper
      const isRepeatArray =
        Array.isArray(question.response) &&
        question.response.length > 0 &&
        "assessmentResponse" in question.response[0];

      if (isRepeatArray) {
        const active = this.getActiveResponse(question);
        return active ? active.assessmentResponse : null;
      }

      // If it's a normal direct response, just return the response object itself
      return question.response;
    },
    isSeparateFarmRespondent(question) {
      return (
        question?.respondentType ===
        "Farmer (Separate response for each farm)"
      );
    },
    getScopedQuestions(question) {
      if (!this.isSeparateFarmRespondent(question)) {
        const activeResponse = this.getActiveResponse(question);
        return [
          {
            ...question,
            __scopeKey: question?._id || "default-question-scope",
            __isSeparateFarmScope: false,
            rejectionReason: activeResponse?.rejectionReason || question.rejectionReason || (question.response && !Array.isArray(question.response) ? question.response.rejectionReason : null),
          },
        ];
      }

  
  const activeResponse = this.getActiveResponse(question);
  const activeDate = activeResponse ? activeResponse.repetitionDate : null;

    let responses = Array.isArray(question?.response) ? question.response : [];

  if (activeDate) {
    responses = responses.filter(r => r.repetitionDate === activeDate);
  }

      if (!responses.length) {
        return [
          {
            ...question,
            __scopeKey: `${question?._id || "question"}-empty`,
            __isSeparateFarmScope: true,
            __farmName: "-",
            __responseItem: null,
          },
        ];
      }

      return responses.flatMap((responseItem, responseIndex) => {
        const farms =
          Array.isArray(responseItem?.farms) && responseItem.farms.length
            ? responseItem.farms
            : [{ _id: `farm-${responseIndex}`, farmName: "-" }];

        return farms.map((farm, farmIndex) => ({
          ...question,
          __scopeKey: `${question?._id || "question"}-${responseItem?.responseId || responseIndex}-${farm?._id || farmIndex}`,
          __isSeparateFarmScope: true,
          __farmName: farm?.farmName || "-",
          __responseItem: responseItem, // This ensures the UI child-components get the correct data object
          rejectionReason: responseItem?.rejectionReason || null,
        }));
      });
    },
    getActiveRepeatWrapper(question) {
      if (Object.prototype.hasOwnProperty.call(question || {}, "__responseItem")) {
        return question?.__responseItem || null;
      }

      const isRepeatArray =
        Array.isArray(question?.response) &&
        question.response.length > 0 &&
        "assessmentResponse" in question.response[0];

      if (!isRepeatArray) return null;
      return this.getActiveResponse(question);
    },
    getRepeatSubmissions(question) {
      if (Object.prototype.hasOwnProperty.call(question || {}, "__responseItem")) {
        return [];
      }

      if (!question.repetitionDates || !question.repetitionDates.length)
        return [];

      const isRepeatArray =
        Array.isArray(question.response) &&
        question.response.length > 0 &&
        "assessmentResponse" in question.response[0];

      if (!isRepeatArray) return [];

      const activeDate =
        question.selectedDate ||
        (question.response && question.response.length
          ? question.response[question.response.length - 1].repetitionDate
          : null);
      return question.repetitionDates.map((dateStr, index) => {
        const match = (question.response || []).find(
          (r) => r.repetitionDate === dateStr,
        );
        let status;
        let dateDisplay = "";
        if (match) {
          status = match.rejectionReason ? "rejected" : "active";
          const d = new Date(dateStr);
          dateDisplay =
            d
              .toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
              .toUpperCase()
              .replace(/ /g, "-") +
            " | " +
            d.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            });
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const slotDate = new Date(dateStr);
          slotDate.setHours(0, 0, 0, 0);
          const nextDateStr = question.repetitionDates[index + 1];
          const nextDate = nextDateStr ? new Date(nextDateStr) : null;
          if (nextDate) nextDate.setHours(0, 0, 0, 0);

          if (slotDate > today) {
            status = "upcoming";
          } else if (!nextDate || nextDate > today) {
            status = "not-yet";
          } else {
            status = "not-submitted";
          }
        }
        return {
          title: `Submission: ${index + 1} of ${
            question.repetitionDates.length
          }`,
          date: dateDisplay,
          status: status,
          rejectionReason: match ? match.rejectionReason : null,
          rawDate: dateStr,
          isSelected: activeDate === dateStr,
        };
      });
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("DD-MMM-YYYY | HH:mm:ss").toUpperCase();
    },
    formatDateShort(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("DD-MM-YYYY");
    },

    goBack() {
      this.$router.go(-1);
    },
    goToDeforestationReport(id) {
        if (!id) return;
        this.$router.push({
          name: "EudrAssessmentReport",
          params: { id, type: "assignment" },
        });
    },
    getDimitraModuleDisplayName(key) {
      const nameMap = {
        Soil: "Soil Management",
      };
      const displayName = nameMap[key] || key;
      const catalogKey = `esgPlatform.dimitraModuleCatalog.modules.${key}`;
      const translated = this.$t(catalogKey);
      return translated !== catalogKey ? translated : displayName;
    },
    async getSurveyResponseByGoalId() {
      this.loading = true;
      try {
        const submittedById =
          this.submittedById || this.$route.params.submittedById;
        const payload = {
          subOrganizationId: submittedById,
          goalId: this.goalId,
          submittedById,
        };

        if (this.questionId) {
          payload.questionId = this.questionId;
          payload.protocolId =
            this.protocolId || this.assessmentId || this.$route.params.assessmentId;
        }

        const res = await EsgService.getSurveyResponseByGoalId(payload);
        this.surveyData = res.data;
      } catch (error) {
        console.error("Error fetching survey responses:", error);
        this.$notify({ type: "error", text: "Error loading survey responses" });
      } finally {
        this.loading = false;
      }
    },

    // --- Helper Methods ---
    getQuestionResponseValue(question) {
      const val = this.getVal(question);
      if (val === null || val === undefined) return "";
      return this.formatDisplayValue(
        typeof val === "object" && val !== null && "value" in val
          ? val.value
          : val,
        question,
      );
    },
    formatMeasurementValue(value, metadata = {}) {
      if (value === null || value === undefined || value === "") return "";

      const settings =
        metadata?.additionalSettings || metadata?.additionalSetting || {};
      const isObjectValue =
        typeof value === "object" && value !== null && !Array.isArray(value);

      const rawValue = isObjectValue ? value.value ?? "" : value;
      const unit =
        (isObjectValue &&
          (value.abbr ||
            settings.measurementUnit ||
            value.name ||
            value.id)) ||
        settings.measurementUnit ||
        "";

      if (rawValue === null || rawValue === undefined || rawValue === "") {
        return unit || "";
      }

      return unit ? `${rawValue} ${unit}` : String(rawValue);
    },
    formatDisplayValue(value, metadata = {}) {
      if (value === null || value === undefined) return "";
      if (Array.isArray(value)) return value;

      if (
        metadata?.questionType === "measurement-unit" ||
        metadata?.dataType === "measurement-unit"
      ) {
        return this.formatMeasurementValue(value, metadata);
      }

      if (typeof value === "object" && value !== null) {
        if (value.label) return value.label;
        if (value.title) return value.title;
        if (Object.prototype.hasOwnProperty.call(value, "value")) {
          const nestedValue = value.value;
          if (
            typeof nestedValue === "object" &&
            nestedValue !== null &&
            !Array.isArray(nestedValue)
          ) {
            return nestedValue.label || nestedValue.title || nestedValue.id || "";
          }
          return nestedValue ?? "";
        }
        return value.id || "";
      }

      return value;
    },
    getSelectedRadioValue(question) {
      const val = this.getVal(question);
      if (!val) return null;
      if (val.value && val.value.id) return val.value.id;
      if (val.value) return val.value;
      if (val.id) return val.id;
      return val;
    },
    getSelectedRadioOption(question) {
      const id = this.getSelectedRadioValue(question);
      if (!id) return null;
      return question.options?.find((opt) => opt._id === id || opt.id === id);
    },
    normalizeActionPlanResponses(actionPlans = []) {
      if (!Array.isArray(actionPlans)) return [];

      return actionPlans
        .filter((plan) => plan && (plan.title || Array.isArray(plan.files)))
        .map((plan) => ({
          id: plan.id || plan._id,
          title: plan.title || "-",
          files: Array.isArray(plan.files) ? plan.files : [],
        }));
    },
    getFileUrl(file) {
      return file?.fileUrl || file?.attachmentLink || "";
    },
    getFileDisplayName(file, fallback = "File") {
      return (
        file?.fileName ||
        file?.name ||
        (file?.key ? file.key.split("/").pop() : "") ||
        fallback
      );
    },
    triggerFileDownload(url, filename, shouldRevoke = false) {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename || "download";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      if (shouldRevoke) {
        window.URL.revokeObjectURL(url);
      }
    },
    async forceFileDownload(url, filename) {
      const response = await fetch(encodeURI(url), {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      this.triggerFileDownload(blobUrl, filename, true);
    },
    forceImageDownload(url, filename) {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          this.$notify({
            type: "error",
            text: "Unable to download this file right now.",
          });
          return;
        }

        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (!blob) {
            this.$notify({
              type: "error",
              text: "Unable to download this file right now.",
            });
            return;
          }

          const blobUrl = window.URL.createObjectURL(blob);
          this.triggerFileDownload(blobUrl, filename, true);
        });
      };

      img.onerror = () => {
        this.$notify({
          type: "error",
          text: "Unable to download this file right now.",
        });
      };

      img.src = `${encodeURI(url)}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
    },
    async downloadFile(file) {
      const url = this.getFileUrl(file);
      if (!url) return;

      const filename = this.getFileDisplayName(file, "download");

      try {
        if (this.isImage(file)) {
          this.forceImageDownload(url, filename);
          return;
        }

        await this.forceFileDownload(url, filename);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.$notify({
          type: "error",
          text: "Unable to download this file right now.",
        });
      }
    },
    getRadioActionPlanResponses(question) {
      const val = this.getVal(question);
      return this.normalizeActionPlanResponses(val?.actionPlanResponses);
    },
    getNestedDataResponse(question, dataQId, isFile = false) {
      const val = this.getVal(question);
      if (!val || !val.getDataResponses) return isFile ? [] : "";
      const found = val.getDataResponses.find((r) => r.id === dataQId);
      if (!found) return isFile ? [] : "";
      if (isFile) return found.value; // Return object/array for files
      const dataQuestion = this.getSelectedRadioOption(question)?.getDataQuestions?.find(
        (item) => item._id === dataQId || item.id === dataQId,
      );
      return this.formatDisplayValue(found.value, {
        ...(dataQuestion || {}),
        ...found,
      });
    },
    isOptionSelected(question, option) {
      const val = this.getVal(question);
      if (!val) return false;

      let arr = [];
      if (Array.isArray(val)) {
        arr = val;
      } else if (Array.isArray(val.value)) {
        arr = val.value;
      } else {
        return false;
      }

      return arr.some((r) => {
        const rId = r.id || r.value?.id || r;
        return rId === (option._id || option.id);
      });
    },
    getCheckboxNestedDataResponse(question, option, dataQId, isFile = false) {
      const val = this.getVal(question);
      if (!val) return isFile ? [] : "";
      const arr = Array.isArray(val)
        ? val
        : Array.isArray(val.value)
        ? val.value
        : [];
      const optionResp = arr.find(
        (r) =>
          r.id === (option._id || option.id) ||
          r.value?.id === (option._id || option.id),
      );
      if (!optionResp || !optionResp.getDataResponses) return isFile ? [] : "";
      const found = optionResp.getDataResponses.find((r) => r.id === dataQId);
      if (!found) return isFile ? [] : "";
      if (isFile) return found.value; // Return object/array for files
      const dataQuestion = option.getDataQuestions?.find(
        (item) => item._id === dataQId || item.id === dataQId,
      );
      return this.formatDisplayValue(found.value, {
        ...(dataQuestion || {}),
        ...found,
      });
    },
    getCheckboxActionPlanResponses(question, option) {
      const val = this.getVal(question);
      if (!val) return [];

      const arr = Array.isArray(val)
        ? val
        : Array.isArray(val.value)
        ? val.value
        : [];

      const optionResp = arr.find(
        (r) =>
          r.id === (option._id || option.id) ||
          r.value?.id === (option._id || option.id),
      );

      return this.normalizeActionPlanResponses(optionResp?.actionPlanResponses);
    },
    getDimitraModuleCatalog(moduleName) {
      return DIMITRA_CONFIG_TEMPLATE.dimitraModuleCatalog?.[moduleName] || [];
    },
    formatDimitraFieldLabel(key, moduleName = "") {
      const catalogField = this.getDimitraModuleCatalog(moduleName).find(
        (field) => field.key === key,
      );
      if (catalogField?.label) return catalogField.label;
      if (key === "id") return "ID";

      return String(key || "")
        .replace(/[_-]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    getTranslatedDimitraFieldLabel(field) {
      if (!field || !field.label) return "";

      const labelToKeyMap = {
        "Date of Entry": "date-of-entry",
        "Area": "area",
        "Area Planted": "area-planted",
        "Start Date of Harvesting": "start_date_harvesting",
        "Farm Name": "farm-name",
        "ID": "id",
      };

      const key =
        field.key ||
        labelToKeyMap[field.label] ||
        field.label.toLowerCase().replace(/\s+/g, "-");

      const catalogKey = `esgPlatform.dimitraModuleCatalog.fields.${key}`;
      const translated = this.$t(catalogKey);

      if (translated && translated !== catalogKey) {
        return translated;
      }

      return this.$t(`esgPlatform.assessmentFarmer.${field.label}`) || field.label;
    },
    formatDimitraFieldValue(value, meta = {}) {
      if (value === null || value === undefined || value === "") return "-";

      const metaKey = String(meta?.key || "").trim().toLowerCase();
      if (metaKey === "location" && typeof value === "object" && value) {
        const lat = parseFloat(value?.lat ?? value?.latitude);
        const lng = parseFloat(value?.log ?? value?.lng ?? value?.longitude);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return "Point";
        }
      }

      if (metaKey === "farmgeofence" && Array.isArray(value)) {
        const coords = value
          .map((coord) => {
            const lat = parseFloat(coord?.lat ?? coord?.latitude);
            const lng = parseFloat(coord?.log ?? coord?.lng ?? coord?.longitude);
            if (Number.isFinite(lat) && Number.isFinite(lng)) {
              return { lat, lng };
            }
            return null;
          })
          .filter(Boolean);
        return coords.length >= 3 ? "Polygon" : "-";
      }

      if (Array.isArray(value)) {
        const formatted = value
          .map((item) => this.formatDimitraFieldValue(item, meta))
          .filter((item) => item && item !== "-");
        return formatted.length ? formatted.join(", ") : "-";
      }

      if (typeof value === "object") {
        return JSON.stringify(value);
      }

      if (
        meta?.valueType === "date" &&
        moment(value, moment.ISO_8601, true).isValid()
      ) {
        return moment(value).format("DD-MMM-YYYY");
      }

      const formattedValue = String(value);
      if (meta?.unit) {
        return `${formattedValue} ${meta.unit}`;
      }

      return formattedValue;
    },
    getDimitraRowDisplayFields(moduleName, row = {}) {
      const catalog = this.getDimitraModuleCatalog(moduleName);
      const catalogKeys = catalog.map((field) => field.key);

      const orderedFields = catalog
        .filter((field) => row[field.key] !== undefined)
        .map((field) => {
          let displayLabel = field.label;
          if (moduleName === "Soil Management" && field.key === "farmId") {
            displayLabel = "Farm Name";
          }

          return {
            key: field.key,
            label: displayLabel,
            value: this.formatDimitraFieldValue(row[field.key], {
              ...field,
              key: field.key,
            }),
            rawValue: row[field.key],
          };
        });

      const extraFields = Object.keys(row)
        .filter((key) => !catalogKeys.includes(key) && key !== 'deforestationStatus' && key !== 'id')
        .map((key) => {
          // Check for farmId in extra fields as well, just in case
          let displayLabel = this.formatDimitraFieldLabel(key, moduleName);
          if (moduleName === "Soil Management" && key === "farmId") {
            displayLabel = "Farm Name";
          }

          return {
            key,
            label: displayLabel,
            value: this.formatDimitraFieldValue(row[key], { key }),
            rawValue: row[key],
          };
        });

      return [...orderedFields, ...extraFields];
    },
    isMeaningfulDimitraField(field) {
      return !!(
        field &&
        field.value !== undefined &&
        field.value !== null &&
        field.value !== "" &&
        field.value !== "-"
      );
    },
    getMeaningfulDimitraFields(moduleName, row = {}) {
      return this.getDimitraRowDisplayFields(moduleName, row).filter((field) =>
        this.isMeaningfulDimitraField(field),
      );
    },
    getDimitraCardHeading(moduleName, row = {}, rowIdx = 0) {
      const fieldMap = new Map(
        this.getMeaningfulDimitraFields(moduleName, row).map((field) => [
          field.key,
          field,
        ]),
      );
      const candidateKeys = [
        "irrigation_farm",
        "farm",
        "farmName",
        "name",
        "title",
        "harvest_cropType",
        "irrigation_cropType",
        "farmId",
        "id",
      ];

      for (const key of candidateKeys) {
        const field = fieldMap.get(key);
        if (field) {
          return {
            key,
            title: this.getCompactDimitraValue(field.value),
          };
        }
      }

      const firstField = this.getMeaningfulDimitraFields(moduleName, row)[0];
      if (firstField) {
        return {
          key: firstField.key,
          title: this.getCompactDimitraValue(firstField.value),
        };
      }

      return {
        key: null,
        title: `${moduleName} #${row?.id || rowIdx + 1}`,
      };
    },
    getCompactDimitraValue(value) {
      const normalizedValue = Array.isArray(value)
        ? value.join(", ")
        : String(value ?? "");
      const compactValue = normalizedValue.split(",")[0].trim();
      return compactValue.length > 42
        ? `${compactValue.slice(0, 39)}...`
        : compactValue || "-";
    },
    getDimitraCardTitle(moduleName, row = {}, rowIdx = 0) {
      return this.getDimitraCardHeading(moduleName, row, rowIdx).title;
    },
    getDimitraPreviewFields(moduleName, row = {}, rowIdx = 0) {

       const fields = this.getMeaningfulDimitraFields(moduleName, row);
        if (fields.length <= 2) {
          return fields.slice(0, 2);
      }

      const headingKey = this.getDimitraCardHeading(moduleName, row, rowIdx).key;
      const filteredFields = fields.filter((field) => field.key !== headingKey);
      if(moduleName === "Soil Management" || moduleName === "Nutrient Management"){
      return (filteredFields.length ? filteredFields : fields).slice(0, 4);
      }
      else{
        return (filteredFields.length ? filteredFields : fields).slice(0, 2);
      }
    },
    getDimitraHiddenFieldCount(moduleName, row = {}, rowIdx = 0) {
      const fields = this.getMeaningfulDimitraFields(moduleName, row);
      if (fields.length <= 2) {
        return 0;
      }

      const previewKeys = new Set(
        this.getDimitraPreviewFields(moduleName, row, rowIdx).map(
          (field) => field.key,
        ),
      );

      return fields.filter((field) => !previewKeys.has(field.key)).length;
    },
    shouldShowDimitraDetailsButton(moduleName, row = {}) {
      return this.getMeaningfulDimitraFields(moduleName, row).length > 2;
    },
    openDimitraDetailsDialog(moduleName, row = {}, rowIdx = 0) {
      // -- Irrigation module: redirect to dedicated irrigation details page --
      if (String(moduleName).trim().toLowerCase() === "irrigation") {
        // Collect all irrigation rows from the active question's dimitraModule data.
        // Walk the normalizedResponses to find any question that has Irrigation rows.
        let allIrrigationRows = [];
        const headings = this.normalizedResponses || [];
        outer: for (const heading of headings) {
          for (const question of (heading.assessmentQuestions || [])) {
            const dimitraModule =
              question.dimitraModule || question.dimitraModules || {};
            const irrigationData = dimitraModule["Irrigation"];
            if (irrigationData && Array.isArray(irrigationData.rows) && irrigationData.rows.length) {
              allIrrigationRows = irrigationData.rows;
              break outer;
            }
          }
        }

        // Fallback: use the single row if we couldn't find the full list
        if (!allIrrigationRows.length && row && typeof row === "object") {
          allIrrigationRows = [row];
        }

        const submittedUser = this.surveyData?.submittedUser || {};
        const farmerName = [submittedUser.firstName, submittedUser.lastName]
          .filter(Boolean)
          .join(" ") || submittedUser?.company?.name || "";

        const irrigationPayload = {
          assessmentId: this.assessmentId,
          goalId: this.goalId,
          submittedById: this.submittedById,
          irrigationRows: allIrrigationRows,
          farmerName,
        };

        window.sessionStorage.setItem(
          "assessmentFarmerIrrigationDetails",
          JSON.stringify(irrigationPayload),
        );

        this.$router.push({
          name: "assessmentFarmerIrrigationDetails",
          params: {
            assessmentId: this.assessmentId,
            goalId: this.goalId,
            submittedById: this.submittedById,
          },
        });
        return;
      }
      // -- End Irrigation redirect --

      const dimitraDetails = {
        assessmentId: this.assessmentId,
        goalId: this.goalId,
        submittedById: this.submittedById,
        moduleName,
        title: this.getDimitraCardTitle(moduleName, row, rowIdx),
        fields: this.getMeaningfulDimitraFields(moduleName, row),
        returnRoute: {
          name: "assessmentFarmerResponses",
          params: {
            assessmentId: this.assessmentId,
            goalId: this.goalId,
            submittedById: this.submittedById,
          },
          query: {
            ...(this.questionId ? { questionId: this.questionId } : {}),
            ...(this.protocolId ? { protocolId: this.protocolId } : {}),
            ...(this.organizationId ? { organizationId: this.organizationId } : {}),
          },
        },
      };

      window.sessionStorage.setItem(
        "assessmentFarmerDimitraDetails",
        JSON.stringify(dimitraDetails),
      );

      this.$router.push({
        name: "assessmentFarmerDimitraDetails",
        params: {
          assessmentId: this.assessmentId,
          goalId: this.goalId,
          submittedById: this.submittedById,
        },
      });
    },
    isFarmModule(moduleName) {
      return String(moduleName || "").trim().toLowerCase() === "farms";
    },
    getFarmGeofenceCoordinates(row = {}) {
      const raw = Array.isArray(row?.farmGeofence) ? row.farmGeofence : [];
      return raw
        .map((coord) => {
          const lat = parseFloat(coord?.lat ?? coord?.latitude);
          const lng = parseFloat(coord?.log ?? coord?.lng ?? coord?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng };
          }
          return null;
        })
        .filter(Boolean);
    },
    shouldShowFarmGeofenceLink(moduleName, row = {}) {
      if (!this.isFarmModule(moduleName)) return false;
      return this.getFarmGeofenceCoordinates(row).length >= 3;
    },
    openFarmGeofenceDialog(moduleName, row = {}, rowIdx = 0) {
      const coordinates = this.getFarmGeofenceCoordinates(row);
      if (coordinates.length < 3) return;

      return this.openFarmGeofenceDialogWithTitle(
        this.getDimitraCardTitle(moduleName, row, rowIdx),
        row?.farmGeofence,
      );
    },
    openFarmGeofenceDialogWithTitle(title, farmGeofence) {
      const coordinates = this.getFarmGeofenceCoordinates({ farmGeofence });
      if (coordinates.length < 3) return;
      this.farmGeofenceDetails = {
        moduleName: "Farms",
        title: title || "Farm Geofencing",
        coordinates,
      };
      this.farmGeofenceDialog = true;
    },
    closeFarmGeofenceDialog() {
      this.farmGeofenceDialog = false;
    },
    async renderFarmGeofenceMap() {
      try {
        const mapContainer = this.$refs.farmGeofenceMap;
        const coordinates = this.farmGeofenceDetails?.coordinates || [];
        if (!mapContainer || coordinates.length < 3) return;

        const loader = getGoogleMapsLoader();
        const google = await loader.load();
        this.google = google;

        const first = coordinates[0];
        this.farmGeofenceMap = new google.maps.Map(mapContainer, {
          mapTypeId: "satellite",
          center: { lat: first.lat, lng: first.lng },
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
        });

        if (this.farmGeofencePolygon) {
          this.farmGeofencePolygon.setMap(null);
        }

        this.farmGeofencePolygon = new google.maps.Polygon({
          paths: coordinates,
          strokeColor: "#FF6B00",
          strokeOpacity: 0.85,
          strokeWeight: 2,
          fillColor: "#FF6B00",
          fillOpacity: 0.2,
          clickable: false,
          editable: false,
        });
        this.farmGeofencePolygon.setMap(this.farmGeofenceMap);

        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach((coord) => bounds.extend(coord));
        this.farmGeofenceMap.fitBounds(bounds, 60);
      } catch (e) {
        // fail silently; dialog will still open
      }
    },
    teardownFarmGeofenceMap() {
      try {
        if (this.farmGeofencePolygon) {
          this.farmGeofencePolygon.setMap(null);
        }
      } catch (e) {
        // ignore
      }
      this.farmGeofencePolygon = null;
      this.farmGeofenceMap = null;
    },
    getDimitraModuleKeys(source = {}) {
      const ignoredKeys = new Set([
        "app",
        "modules",
        "fieldsByModule",
        "selectionAction",
        "expanded",
        "progressMetrics",
        "score",
        "activeModuleIndex",
      ]);
      const directKeys = Object.keys(source || {}).filter((key) => !ignoredKeys.has(key));
      const configuredKeys = Array.isArray(source?.modules) ? source.modules : [];
      return [...new Set([...directKeys, ...configuredKeys])];
    },
    buildDimitraModuleEntries(selectionMap = {}, moduleSource = {}) {
      const moduleNames = new Set([
        ...this.getDimitraModuleKeys(selectionMap),
        ...this.getDimitraModuleKeys(moduleSource),
      ]);

      return [...moduleNames].reduce((acc, moduleName) => {
        const selectedValue = selectionMap?.[moduleName];
        const sourceValue = moduleSource?.[moduleName];
        const selectedIds = Array.isArray(selectedValue) ? selectedValue : [];
        const sourceRows = Array.isArray(sourceValue?.rows)
          ? sourceValue.rows
          : [];
        const selectedRows = Array.isArray(selectedValue?.rows)
          ? selectedValue.rows
          : [];

        let rows = [];

        if (selectedIds.length) {
          const sourceMap = new Map(
            [...sourceRows, ...selectedRows]
              .filter((row) => row && row.id !== undefined && row.id !== null)
              .map((row) => [row.id, row]),
          );
          rows = selectedIds.map((id) => sourceMap.get(id)).filter(Boolean);
        } else if (selectedRows.length) {
          rows = selectedRows;
        } else if (sourceRows.length && !Array.isArray(sourceValue)) {
          rows = sourceRows;
        }

        if (rows.length) {
          acc[moduleName] = rows;
        }

        return acc;
      }, {});
    },
    hasDimitraModuleRows(moduleEntries) {
      return Object.values(moduleEntries || {}).some(
        (rows) => Array.isArray(rows) && rows.length,
      );
    },
    getDimitraModuleEntries(question) {
      const val = this.getVal(question);
      if (!val) return {};
      const activeWrapper = this.getActiveRepeatWrapper(question);

      return this.buildDimitraModuleEntries(
        val,
        activeWrapper?.dimitraModule ||
          activeWrapper?.dimitraModules ||
          question.dimitraModule ||
          question.dimitraModules ||
          val,
      );
    },
    getRadioDimitraModuleEntries(question) {
      const val = this.getVal(question);
      const activeWrapper = this.getActiveRepeatWrapper(question);
      return this.buildDimitraModuleEntries(
        val?.dimitraModule || val?.dimitraModules,
        activeWrapper?.dimitraModule ||
          activeWrapper?.dimitraModules ||
          question.dimitraModule ||
          question.dimitraModules ||
          {},
      );
    },
    getCheckboxDimitraModuleEntries(question, option) {
      const val = this.getVal(question);
      if (!val) return {};
      const activeWrapper = this.getActiveRepeatWrapper(question);

      const arr = Array.isArray(val)
        ? val
        : Array.isArray(val.value)
        ? val.value
        : [];

      const optionResp = arr.find(
        (r) =>
          r.id === (option._id || option.id) ||
          r.value?.id === (option._id || option.id),
      );

      return this.buildDimitraModuleEntries(
        optionResp?.dimitraModule || optionResp?.dimitraModules,
        activeWrapper?.dimitraModule ||
          activeWrapper?.dimitraModules ||
          question.dimitraModule ||
          question.dimitraModules ||
          optionResp?.dimitraModule ||
          optionResp?.dimitraModules ||
          {},
      );
    },
    getRootGetDataResponse(question, dataQId, isFile = false) {
      const val = this.getVal(question);
      if (!val) return isFile ? [] : "";
      const arr = Array.isArray(val)
        ? val
        : Array.isArray(val.value)
        ? val.value
        : [];
      const found = arr.find(
        (r) => r.id === dataQId || r.value?.id === dataQId,
      );
      if (!found) return isFile ? [] : "";
      if (isFile) return found.value; // Return object/array for files
      const dataQuestion = question.getDataQuestions?.find(
        (item) => item._id === dataQId || item.id === dataQId,
      );
      return this.formatDisplayValue(found.value, {
        ...(dataQuestion || {}),
        ...found,
      });
    },
    extractUploadedFilesFromResponse(question) {
      const val = this.getVal(question);
      if (!val) return [];
      let files = [];
      if (Array.isArray(val)) files = val;
      else if (val.value)
        files = Array.isArray(val.value) ? val.value : [val.value];
      else files = [val];

      return files
        .filter((f) => f && (f.fileName || f.name || f.key || f.s3Key))
        .map((f) => ({
          fileName: f.fileName || f.name || f.key?.split("/").pop(),
          fileUrl: f.fileUrl || f.attachmentLink,
          comments: f.comments || { text: f.comment },
        }));
    },
    getRecommendationsList(question) {
      if (!question.options) return [];
      if (question.questionType === "radio-button") {
        const id = this.getSelectedRadioValue(question);
        return question.options
          .filter(
            (opt) => (opt._id === id || opt.id === id) && opt.recommendation,
          )
          .map((opt) => opt.recommendation);
      } else if (question.questionType === "check-boxes") {
        return question.options
          .filter(
            (opt) => this.isOptionSelected(question, opt) && opt.recommendation,
          )
          .map((opt) => opt.recommendation);
      }
      return [];
    },
    getFileType(file) {
      if (!file) return "PDF";
      if (file.fileType) return file.fileType.toUpperCase();
      const urlPath =
        this.getFileDisplayName(file, "") ||
        file.key ||
        this.getFileUrl(file) ||
        "";
      const match = urlPath.match(/\.([^.]+)$/);
      if (match && match[1]) {
        const ext = match[1].split("?")[0]; // Strip url query params
        return ext.toUpperCase();
      }
      return "DOC";
    },
    isImage(file) {
      const ext = this.getFileType(file).toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext);
    },
    getStatusClass(status) {
    const map = {
      'Very High Deforestation Probability': 'v-high-prob',
      'High Deforestation Probability': 'high-prob',
      'Medium Deforestation Probability': 'medium-prob',
      'Low Deforestation Probability': 'low-prob',
      'Very Low Deforestation Probability': 'v-low-prob',
      'Zero/Negligible Deforestation Probability': 'zero-prob',
      'Manually Mitigated': 'manual-prob'
    };

    return map[status] || '';
  }
  },
};
</script>

<style scoped>
.water-scarcity-section {
  background-color: #f4f5f7;
  padding: 24px 24px 1px 24px;
  border-radius: 4px;
  margin-bottom: 24px;
}
.radio-box-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #ffffff;
  transition: all 0.2s;
}
.radio-box-item:hover {
  border-color: #bdbdbd;
}
.pdf-file-box {
  background-color: #e5f8f1;
  border: 1px solid transparent;
  color: #135b00;
  border-radius: 6px;
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.2s;
}
.pdf-file-box:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}
.file-preview-box {
  width: 104px;
  height: 104px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5f8f1;
  position: relative;
}
.file-preview-trigger {
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.file-preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(229, 248, 241, 0.94);
  color: #135b00;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}
.file-preview-overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.file-preview-extension {
  color: #135b00;
  line-height: 1;
}
.file-preview-box:hover .file-preview-overlay,
.file-preview-box:focus-within .file-preview-overlay {
  opacity: 1;
}
.file-link-button,
.action-link-button {
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}
.file-link-button:hover,
.action-link-button:hover {
  text-decoration: underline;
}
.action-link {
  text-decoration: none;
}
.action-link:hover {
  text-decoration: underline;
}
.recommendations-box {
  border: 1px solid #ffcc80;
  border-radius: 4px;
  background-color: #fffdf8;
}
.action-plan-box {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: #ffffff;
}
.dimitra-module-heading {
  color: #212121;
  letter-spacing: 0.02em;
}
.dimitra-summary-card {
  height: 100%;
  border: 1px solid #e7ece8;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0px 2px 10px 0px #50505029 !important;
}
.dimitra-summary-title {
  color: #212121;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  min-height: 48px;
}
.dimitra-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
}
.dimitra-summary-field {
  min-width: 0;
}
.dimitra-summary-field--full {
  grid-column: 1 / -1;
}
.dimitra-summary-label {
  color: #7b857d;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
}
.dimitra-summary-value {
  color: #212121;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}
.dimitra-geo-chip {
display: inline-flex;
    align-items: center;
    padding: 4px 0px;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 6px;
    margin-right: 10px;
    width: fit-content;
}
.dimitra-summary-more {
  border-top: 1px solid #e9efeb;
  color: #6a756d;
  font-size: 12px;
  line-height: 1.5;
  padding-top: 14px;
}
.dimitra-summary-empty {
  color: #6a756d;
  font-size: 13px;
}
.status-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  max-width: fit-content;
  padding: 4px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}
.v-high-prob{
  border: 1px solid #8B0000;
  background: #FF8F8F;
  color: #8B0000;
}
.high-prob{
  border: 1px solid #8B0000;
  background: #FFB6B6;
  color: #8B0000;
}
.medium-prob{
  border: 1px solid #F06B20;
  background: #FFCEB3;
  color: #F06B20;
}
.low-prob{
  border: 1px solid #F06B20;
  background: #FFE8C5;
  color: #F06B20;
}
.v-low-prob{
  border: 1px solid #F49238;
  background: #FFFCEC;
  color: #F49238;
}
.zero-prob{
  border: 1px solid #00BD73;
  background: #E2FFF4;
  color: #00BD73;
}
.manual-prob{
  border: 1px solid #00BD73;
  background: #E2FFF4;
  color: #00BD73;
}
.dimitra-detail-btn {
  border-color: #00bd73 !important;
  color: #00bd73 !important;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  min-width: 132px;
}
.dimitra-detail-btn:hover {
  background-color: #f2fdf8;
}
.dimitra-map-link {
  color: #00bd73;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
}
.dimitra-map-link:hover {
  text-decoration: underline;
}
.farm-geofence-map {
  width: 100%;
  height: 70vh;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #edf1ee;
}
.dimitra-detail-dialog {
  border-radius: 20px;
}
.dimitra-detail-dialog-header {
  border-bottom: 1px solid #edf1ee;
}
.dimitra-detail-overline {
  color: #00bd73;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.dimitra-detail-title {
  color: #212121;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
}

/* Repeat Submission Box Styles */
.repeat-submission-list {
  width: 100%;
}
.repeat-box-item {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background-color: #ffffff;
  transition: all 0.2s;
}
.repeat-box-item.active {
  border: 1px solid #00bd73;
  box-shadow: 0 0 4px rgba(0, 189, 115, 0.1);
}
.repeat-content {
  line-height: 1.2;
}
.separate-farm-answer {
  border: 1px solid #e1eee8;
  border-radius: 8px;
}
.rejection-block {
  border: 1px solid #ff4d4f;
  border-radius: 6px;
  padding: 12px 16px;
  background-color: #f8f0f0;
  font-family: sans-serif;
  margin-bottom: 20px;
}
.rejection-title {
  font-weight: 600;
  font-size: 14px;
  color: #ff4d4f;
  margin-bottom: 8px;
}
.rejection-reason {
  font-size: 13px;
  color: #333;
}
.rejection-reason span {
  font-weight: 600;
}

@media (max-width: 600px) {
  .dimitra-summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .dimitra-summary-title {
    min-height: auto;
  }
}
</style>
